<template>
  <div class="items-shell">
    <!-- HERO -->
    <section class="items-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Items</h1>
        <p class="hero-copy">
          Manage wine, beer, spirits, food, supplies, and anything else that flows through the bar.
        </p>
      </div>

      <button @click="openAddPopup()" class="primary-action">
        + New Item
      </button>
    </section>

    <!-- TOOLBAR -->
    <section class="items-toolbar">
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input
          type="text"
          v-model="filters.item_name"
          placeholder="Search item name..."
        />
      </div>

      <div class="search-wrap">
        <span class="search-icon">#</span>
        <input
          type="text"
          v-model="filters.item_no"
          placeholder="Item no..."
        />
      </div>

      <div class="search-wrap">
        <span class="search-icon">✦</span>
        <input
          type="text"
          v-model="filters.details"
          placeholder="Search details..."
        />
      </div>
    </section>

    <!-- ITEM CARDS -->
    <section v-if="sortedItems.length > 0" class="item-grid">
      <article
        v-for="item in sortedItems"
        :key="item.item_no"
        class="item-card"
      >
        <div class="item-main">
          <div class="item-title-row">
            <div>
              <p class="label">Item #{{ item.item_no }}</p>
              <h2>{{ item.item_name }}</h2>
            </div>

            <span class="status-pill">
              {{ item.wine_type || item.category_name || 'Item' }}
            </span>
          </div>

          <div class="item-details">
            <div>
              <span>Details</span>
              <strong>{{ item.details || '—' }}</strong>
            </div>

            <div>
              <span>Category</span>
              <strong>{{ item.category_name || '—' }}</strong>
            </div>

            <div>
              <span>Vintage</span>
              <strong>{{ item.vintage || '—' }}</strong>
            </div>

            <div>
              <span>Bottle Size</span>
              <strong>{{ item.bottle_size || '—' }}</strong>
            </div>

            <div>
              <span>Class</span>
              <strong>{{ item.service_class || '—' }}</strong>
            </div>
          </div>

          <div
            v-if="item.chateau || item.region || item.country || item.cost"
            class="item-subdetails"
          >
            <span v-if="item.chateau">{{ item.chateau }}</span>
            <span v-if="item.region">{{ item.region }}</span>
            <span v-if="item.country">{{ item.country }}</span>
            <span v-if="item.cost">
              ${{ Number(item.cost || 0).toFixed(2) }}
            </span>
          </div>
        </div>

        <div class="card-actions">
          <button @click="openEditPopup(item)" class="ghost-action">
            Edit
          </button>

          <button @click="confirmDelete(item)" class="danger-action">
            Delete
          </button>
        </div>
      </article>
    </section>

    <!-- EMPTY STATE -->
    <section v-else class="empty-state">
      <p class="eyebrow">No Items</p>
      <h2>No matching items found.</h2>
      <p>Add bottles, cases, kegs, food, supplies, or retail merchandise.</p>

      <button @click="openAddPopup()" class="primary-action">
        + New Item
      </button>
    </section>

    <!-- ADD ITEM MODAL -->
    <div v-if="showPopup" class="modal-overlay">
      <div class="item-modal">
        <div class="modal-topline"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">New Inventory Item</p>
            <h2>Add Item</h2>
            <p>Create a new item for purchases, inventory, and sales.</p>
          </div>

          <button type="button" @click="showPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <form @submit.prevent="submitItem" class="modal-body">
          <section class="form-panel">
            <div class="panel-heading">
              <h3>Core Item Info</h3>
              <p>Required basics for item lookup and transactions.</p>
            </div>

            <div class="form-grid">
              <label>
                <span>Item No</span>
                <input
                  type="text"
                  v-model="currentItem.item_no"
                  disabled
                  required
                />
              </label>

              <label>
                <span>Item Name</span>
                <input
                  type="text"
                  v-model="currentItem.item_name"
                  required
                  placeholder="Example: Pinot Noir 750ml"
                />
              </label>

              <label>
                <span>Category / Type</span>
                <select v-model="currentItem.category_id" required>
                  <option value="" disabled>Select a Type</option>
                  <option
                    v-for="category in categories"
                    :key="category.category_id"
                    :value="category.category_id"
                  >
                    {{ category.category_name }}
                  </option>
                </select>
              </label>

              <label>
                <span>Details</span>
                <input
                  type="text"
                  v-model="currentItem.details"
                  placeholder="Short description or notes"
                />
              </label>
            </div>
          </section>

          <section class="form-panel">
            <div class="panel-heading">
              <h3>Wine Details</h3>
              <p>Optional fields for bottles and cellar-style inventory.</p>
            </div>

            <div class="form-grid">
              <label>
                <span>Vintage</span>
                <input
                  type="text"
                  v-model="currentItem.vintage"
                  placeholder="2021"
                />
              </label>

              <label>
                <span>Producer / Chateau</span>
                <input
                  type="text"
                  v-model="currentItem.chateau"
                  placeholder="Producer, winery, chateau..."
                />
              </label>

              <label>
                <span>Country</span>
                <input
                  type="text"
                  v-model="currentItem.country"
                  placeholder="France, USA, Italy..."
                />
              </label>

              <label>
                <span>Region</span>
                <input
                  type="text"
                  v-model="currentItem.region"
                  placeholder="Willamette Valley, Bordeaux..."
                />
              </label>

              <label>
                <span>Bottle Size</span>
                <input
                  type="text"
                  v-model="currentItem.bottle_size"
                  placeholder="750ml, 1.5L, keg..."
                />
              </label>

              <label>
                <span>Wine Type</span>
                <select v-model="currentItem.wine_type" required>
                  <option value="" disabled>Select Wine Type</option>
                  <option
                    v-for="wineType in wineTypeOptions"
                    :key="wineType"
                    :value="wineType"
                  >
                    {{ wineType }}
                  </option>
                </select>
              </label>

              <label>
                <span>Class</span>
                <select v-model="currentItem.service_class" required>
                  <option value="" disabled>Select Class</option>
                  <option
                    v-for="serviceClass in serviceClassOptions"
                    :key="serviceClass"
                    :value="serviceClass"
                  >
                    {{ serviceClass }}
                  </option>
                </select>
              </label>


              <label>
                <span>Cost</span>
                <input
                  type="number"
                  v-model.number="currentItem.cost"
                  step="0.01"
                  placeholder="0.00"
                />
              </label>
            </div>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showPopup = false" class="cancel-action">
              Cancel
            </button>

            <button type="submit" class="primary-action">
              Save Item
            </button>
          </footer>
        </form>
      </div>
    </div>

    <!-- EDIT ITEM MODAL -->
    <div v-if="showEditPopup" class="modal-overlay">
      <div class="item-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Edit Inventory Item</p>
            <h2>Edit Item</h2>
            <p>Update item details used throughout purchases, inventory, and sales.</p>
          </div>

          <button type="button" @click="showEditPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <form @submit.prevent="submitEditItem" class="modal-body">
          <section class="form-panel">
            <div class="panel-heading">
              <h3>Core Item Info</h3>
              <p>Item number is locked once created.</p>
            </div>

            <div class="form-grid">
              <label>
                <span>Item No</span>
                <input
                  type="text"
                  v-model="currentItem.item_no"
                  disabled
                  required
                />
              </label>

              <label>
                <span>Item Name</span>
                <input
                  type="text"
                  v-model="currentItem.item_name"
                  required
                />
              </label>

              <label>
                <span>Category / Type</span>
                <select v-model="currentItem.category_id" required>
                  <option value="" disabled>Select a Type</option>
                  <option
                    v-for="category in categories"
                    :key="category.category_id"
                    :value="category.category_id"
                  >
                    {{ category.category_name }}
                  </option>
                </select>
              </label>

              <label>
                <span>Details</span>
                <input
                  type="text"
                  v-model="currentItem.details"
                />
              </label>
            </div>
          </section>

          <section class="form-panel">
            <div class="panel-heading">
              <h3>Wine Details</h3>
              <p>Optional enrichment fields for better sorting and reporting.</p>
            </div>

            <div class="form-grid">
              <label>
                <span>Vintage</span>
                <input type="text" v-model="currentItem.vintage" />
              </label>

              <label>
                <span>Producer / Chateau</span>
                <input type="text" v-model="currentItem.chateau" />
              </label>

              <label>
                <span>Country</span>
                <input type="text" v-model="currentItem.country" />
              </label>

              <label>
                <span>Region</span>
                <input type="text" v-model="currentItem.region" />
              </label>

              <label>
                <span>Bottle Size</span>
                <input type="text" v-model="currentItem.bottle_size" />
              </label>

              <label>
                <span>Wine Type</span>
                <select v-model="currentItem.wine_type" required>
                  <option value="" disabled>Select Wine Type</option>
                  <option
                    v-for="wineType in wineTypeOptions"
                    :key="wineType"
                    :value="wineType"
                  >
                    {{ wineType }}
                  </option>
                </select>
              </label>

              <label>
                <span>Class</span>
                <select v-model="currentItem.service_class" required>
                  <option value="" disabled>Select Class</option>
                  <option
                    v-for="serviceClass in serviceClassOptions"
                    :key="serviceClass"
                    :value="serviceClass"
                  >
                    {{ serviceClass }}
                  </option>
                </select>
              </label>


              <label>
                <span>Cost</span>
                <input
                  type="number"
                  v-model.number="currentItem.cost"
                  step="0.01"
                />
              </label>
            </div>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showEditPopup = false" class="cancel-action">
              Cancel
            </button>

            <button type="submit" class="primary-action">
              Update Item
            </button>
          </footer>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  data() {
    return {
      items: [],

      showPopup: false,
      showEditPopup: false,

      filters: {
        item_no: '',
        item_name: '',
        details: ''
      },

      currentItem: this.getBlankItem(),

      categories: [],

      wineTypeOptions: [
        'Red Wine',
        'White',
        'Rosé',
        'Sparkling',
        'Rosé Sparkling',
        'Beer',
        'Spirits',
        'Food',
        'Supply',
        'Other'
      ],

      serviceClassOptions: [
        'pour',
        'reserve',
        'bottle',
        'plate',
        'supply',
        'merch',
        'other'
      ]
    };
  },

  computed: {
    sortedItems() {
      return this.filteredItems.slice().sort((a, b) =>
        String(a.item_name || '').localeCompare(String(b.item_name || ''))
      );
    },

    filteredItems() {
      return this.items.filter(item => {
        const itemNo = String(item.item_no || '').toLowerCase();
        const itemName = String(item.item_name || '').toLowerCase();
        const details = String(item.details || '').toLowerCase();

        return (
          (!this.filters.item_no ||
            itemNo.includes(this.filters.item_no.toLowerCase())) &&
          (!this.filters.item_name ||
            itemName.includes(this.filters.item_name.toLowerCase())) &&
          (!this.filters.details ||
            details.includes(this.filters.details.toLowerCase()))
        );
      });
    }
  },

  async mounted() {
    await this.fetchItems();
    await this.fetchCategories();
  },

  methods: {
    getBlankItem() {
      return {
        item_no: '',
        item_name: '',
        details: '',
        vintage: '',
        chateau: '',
        country: '',
        region: '',
        wine_type: '',
        bottle_size: '',
        category_id: '',
        service_class: '',
        cost: null
      };
    },

    async fetchItems() {
      try {
        console.log("🔄 Fetching items...");
        const response = await api.get('/items');
        this.items = response.data || [];
        console.log("📥 Items loaded:", this.items);
      } catch (error) {
        console.error("❌ Error fetching items:", error);
      }
    },

    async fetchCategories() {
      try {
        const response = await api.get('/items/new');
        this.categories = response.data.categories || [];
      } catch (error) {
        console.error("❌ Error fetching categories:", error);
      }
    },

    async openAddPopup() {
      console.log("➕ Add Item button clicked!");

      try {
        const response = await api.get('/items/new');

        this.currentItem = {
          ...this.getBlankItem(),
          item_no: response.data.next_item_no,
          category_id: ''
        };

        this.categories = response.data.categories || [];
        this.showPopup = true;

        this.$nextTick(() => {
          const firstEditableInput = document.querySelector(".item-modal input:not([disabled])");
          if (firstEditableInput) {
            firstEditableInput.focus();
          }
        });
      } catch (error) {
        console.error("❌ Error loading Add Item popup:", error);
      }
    },

    async submitItem() {
      try {
        if (
          !this.currentItem.item_name ||
          !this.currentItem.category_id ||
          !this.currentItem.wine_type ||
          !this.currentItem.service_class
        ) {
          alert("❌ Item Name, Category, Wine Type, and Class are required!");
          return;
        }

        console.log("📤 Submitting new item:", this.currentItem);

        const response = await api.post('/items', {
          item_no: this.currentItem.item_no,
          item_name: this.currentItem.item_name,
          details: this.currentItem.details,
          vintage: this.currentItem.vintage,
          chateau: this.currentItem.chateau,
          country: this.currentItem.country,
          region: this.currentItem.region,
          wine_type: this.currentItem.wine_type,
          bottle_size: this.currentItem.bottle_size,
          category_id: this.currentItem.category_id,
          service_class: this.currentItem.service_class,
          cost: this.currentItem.cost
        });

        console.log("✅ Item successfully added:", response.data);

        this.showPopup = false;
        await this.fetchItems();
      } catch (error) {
        console.error("❌ Error adding item:", error);
        alert("⚠️ Failed to add item. Please check the logs.");
      }
    },

    async openEditPopup(item) {
      console.log("✏️ Editing item:", item.item_no);

      try {
        if (this.categories.length === 0) {
          await this.fetchCategories();
        }

        this.currentItem = {
          ...this.getBlankItem(),
          ...item
        };

        this.showEditPopup = true;
      } catch (error) {
        console.error("❌ Error opening edit popup:", error);
      }
    },

    async submitEditItem() {
      try {

        if (
          !this.currentItem.item_name ||
          !this.currentItem.category_id ||
          !this.currentItem.wine_type ||
          !this.currentItem.service_class
        ) {
          alert("❌ Item Name, Category, Wine Type, and Class are required!");
          return;
        }
        console.log("📤 Submitting item update:", this.currentItem);

        const response = await api.put(`/items/${this.currentItem.item_no}`, {
          item_name: this.currentItem.item_name,
          details: this.currentItem.details,
          vintage: this.currentItem.vintage,
          chateau: this.currentItem.chateau,
          country: this.currentItem.country,
          region: this.currentItem.region,
          wine_type: this.currentItem.wine_type,
          bottle_size: this.currentItem.bottle_size,
          category_id: this.currentItem.category_id,
          service_class: this.currentItem.service_class,
          cost: this.currentItem.cost
        });

        console.log("✅ Item updated successfully:", response.data);

        this.showEditPopup = false;
        await this.fetchItems();
      } catch (error) {
        console.error("❌ Error updating item:", error);
        alert("⚠️ Failed to update item. Please check the logs.");
      }
    },

    async confirmDelete(item) {
      if (!confirm(`Are you sure you want to delete "${item.item_name}"?`)) {
        return;
      }

      console.log("🗑 Attempting to delete item:", item.item_no);

      try {
        await api.delete(`/items/${item.item_no}`);
        alert("✅ Item deleted successfully!");
        await this.fetchItems();
      } catch (error) {
        console.error("❌ Error deleting item:", error);

        const errorMessage =
          error.response?.data?.error || "An unknown error occurred.";

        alert(`❌ Cannot delete item: ${errorMessage}`);
      }
    }
  }
};
</script>

<style scoped>
.items-shell {
  min-height: calc(100vh - 80px);
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* ---------- Hero ---------- */

.items-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.38);
  backdrop-filter: blur(18px);
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.items-hero h1 {
  margin: 0;
  font-size: clamp(40px, 7vw, 72px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 660px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

/* ---------- Toolbar ---------- */

.items-toolbar {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(160px, 0.45fr) minmax(220px, 0.7fr);
  gap: 14px;
  margin: 22px 0;
}

.search-wrap {
  min-height: 56px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: rgba(12, 8, 12, 0.7);
  color: #f7efe8;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}

.search-icon {
  color: #c8a66a;
  font-size: 22px;
  margin-right: 10px;
  line-height: 1;
}

.search-wrap input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: #f7efe8;
  font-size: 16px;
}

.search-wrap input::placeholder {
  color: #8f827e;
}

/* ---------- Cards ---------- */

.item-grid {
  display: grid;
  gap: 18px;
}

.item-card {
  border-radius: 28px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.3);
}

.item-main {
  padding: 24px;
}

.item-title-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.label {
  margin: 0 0 5px;
  color: #9b8f8b;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 900;
}

.item-title-row h2 {
  margin: 0;
  font-size: clamp(24px, 4vw, 34px);
  letter-spacing: -0.045em;
  line-height: 1.02;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
  background: rgba(200, 166, 106, 0.16);
  color: #f1c979;
  border: 1px solid rgba(200, 166, 106, 0.28);
}

.item-details {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 22px;
}

.item-details div {
  min-height: 78px;
  padding: 15px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.item-details span {
  display: block;
  color: #978b86;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 900;
}

.item-details strong {
  display: block;
  margin-top: 8px;
  font-size: 15px;
  color: #fff8f1;
  overflow-wrap: anywhere;
}

.item-subdetails {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.item-subdetails span {
  padding: 8px 12px;
  border-radius: 999px;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
  font-weight: 800;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 24px 24px;
}

/* ---------- Buttons ---------- */

.primary-action,
.cancel-action,
.ghost-action,
.danger-action {
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition:
    transform 0.14s ease,
    box-shadow 0.14s ease,
    background 0.14s ease;
}

.primary-action {
  min-height: 58px;
  padding: 0 26px;
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.cancel-action,
.ghost-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.danger-action {
  background: rgba(155, 45, 45, 0.38);
  border: 1px solid rgba(255, 118, 118, 0.25);
  color: #ffb4a8;
}

.primary-action:hover,
.cancel-action:hover,
.ghost-action:hover,
.danger-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

/* ---------- Empty ---------- */

.empty-state {
  padding: 38px;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px dashed rgba(255, 255, 255, 0.16);
  color: #cfc2bd;
}

.empty-state h2 {
  margin: 0 0 8px;
  color: #fff8f1;
  letter-spacing: -0.035em;
}

.empty-state p {
  margin: 0 0 18px;
}

/* ---------- Modal ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.item-modal {
  width: min(980px, 96vw);
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.11);
  color: #f7efe8;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.modal-topline {
  height: 5px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-topline.gold {
  background: linear-gradient(90deg, #c8a66a, #8f123f);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: clamp(28px, 5vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  overflow-y: auto;
  padding: 22px 28px 28px;
}

.form-panel {
  padding: 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 16px;
}

.panel-heading {
  margin-bottom: 16px;
}

.panel-heading h3 {
  margin: 0;
  font-size: 20px;
  letter-spacing: -0.025em;
}

.panel-heading p {
  margin: 6px 0 0;
  color: #aaa09b;
  font-size: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 14px;
}

.item-modal label {
  display: grid;
  gap: 7px;
  color: #f7efe8;
}

.item-modal label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.item-modal input,
.item-modal select,
.item-modal textarea {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.item-modal input::placeholder,
.item-modal textarea::placeholder {
  color: #766c68;
}

.item-modal input:focus,
.item-modal select:focus,
.item-modal textarea:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.item-modal input:disabled {
  background: rgba(255, 255, 255, 0.045);
  color: #8f827e;
  cursor: not-allowed;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
}

.modal-actions .primary-action,
.modal-actions .cancel-action {
  min-width: 140px;
}

.modal-body::-webkit-scrollbar {
  width: 10px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(200, 166, 106, 0.32);
  border-radius: 999px;
}

/* ---------- Responsive ---------- */

@media (max-width: 1000px) {
  .items-toolbar {
    grid-template-columns: 1fr;
  }

  .item-details {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .items-shell {
    padding: 16px;
  }

  .items-hero,
  .item-title-row,
  .modal-header {
    flex-direction: column;
  }

  .items-hero {
    align-items: stretch;
    padding: 24px;
  }

  .item-details {
    grid-template-columns: 1fr;
  }

  .card-actions,
  .modal-actions {
    flex-direction: column;
  }

  .primary-action,
  .cancel-action,
  .ghost-action,
  .danger-action {
    width: 100%;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .item-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header,
  .modal-body {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>